import { UpperCasePipe } from '@angular/common';
import { Component, effect, EventEmitter, HostListener, inject, Input, OnInit, Output, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { SIDE_NAV_ITEMS, SideNavItem } from '../../../models/side-nav-items';
import { EmployeeService } from '../../../services/employees/employee-service';
import { UserRole } from '../../../models/enums';
import { filter } from 'rxjs';
import { AuthService } from '../../../services/auth/auth-service';
import { NgTemplateOutlet } from '@angular/common';
import { ChangePasswordForm } from '../../../@shared/forms/auth/change-password-form/change-password-form';
import { ReviewStreamService } from '../../../services/review-stream/review-stream-service';

export interface NavGroup {
  label: string;
  items: SideNavItem[];
}

@Component({
  selector: 'app-sidenav',
  imports: [RouterLink, DrawerModule, ButtonModule, TooltipModule, UpperCasePipe, NgTemplateOutlet, MenuModule, ChangePasswordForm],
  templateUrl: './sidenav.html',
  styleUrl: './sidenav.scss'
})
export class Sidenav implements OnInit {

  @Input() drawerVisible = false;
  @Output() drawerVisibleChange = new EventEmitter<boolean>();

  isCollapsed = false;
  groups: NavGroup[] = [];
  currentRoute = signal('');
  showChangePassword = false;

  userMenuItems: MenuItem[] = [
    {
      label: 'Change Password',
      icon: 'fa-solid fa-lock',
      command: () => { this.showChangePassword = true; }
    }
  ];

  private employeeService = inject(EmployeeService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private reviewStream = inject(ReviewStreamService);

  constructor() {
    // Keep badge counts in sync with SSE signals
    effect(() => {
      // The verification page holds both ride photo and operation photo reviews
      const total = (this.reviewStream.verificationCount() ?? 0)
        + (this.reviewStream.operationSubmissionCount() ?? 0);
      this.setBadge('/ride-verification', total > 0 ? total : undefined);
    });
    effect(() => {
      const count = this.reviewStream.damageCount();
      this.setBadge('/damage-reports', count);
    });
  }

  ngOnInit(): void {
    const userRole = this.employeeService.employee()?.role;
    const filtered = userRole ? this.buildMenu(userRole) : SIDE_NAV_ITEMS;
    this.groups = this.groupItems(filtered);

    this.currentRoute.set(this.router.url);
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.currentRoute.set(this.router.url));

    if (userRole) {
      this.reviewStream.start(userRole);
    }
  }


  private setBadge(routePath: string, count: number | undefined): void {
    this.groups.forEach(g => g.items.forEach(item => {
      if (item.routePath === routePath) {
        item.badge = count;
      }
    }));
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    if (this.isCollapsed) {
      this.groups.forEach(g => g.items.forEach(i => i.expanded = false));
    }
  }

  toggleItem(item: SideNavItem): void {
    item.expanded = !item.expanded;
  }

  onDrawerVisibleChange(value: boolean): void {
    this.drawerVisible = value;
    this.drawerVisibleChange.emit(value);
  }

  isActive(item: SideNavItem): boolean {
    if (!item.routePath) return false;
    return this.currentRoute().startsWith(item.routePath);
  }

  get employeeName(): string {
    return this.employeeService.employee()?.fullName ?? '';
  }

  get employeeRole(): string {
    return this.employeeService.employee()?.role ?? '';
  }

  @HostListener('window:resize')
  onResize(): void {
    if (window.innerWidth >= 768 && this.drawerVisible) {
      this.drawerVisible = false;
      this.drawerVisibleChange.emit(false);
    }
  }

  logout(): void {
    const payload = { refreshToken: this.authService.getRefreshToken() };
    this.authService.logout(payload).subscribe({
      next: () => {
        this.reviewStream.stop();
        this.authService.removeTokens();
        this.employeeService.clearEmployee();
        this.router.navigate(['/login']);
      }
    });
  }

  private groupItems(items: SideNavItem[]): NavGroup[] {
    const map = new Map<string, SideNavItem[]>();
    for (const item of items) {
      const key = item.group ?? 'OTHER';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries()).map(([label, items]) => ({ label, items }));
  }

  private buildMenu(role: UserRole): SideNavItem[] {
    return SIDE_NAV_ITEMS.filter(
      item => !item.requiredRoles || item.requiredRoles.includes(role)
    );
  }
}
